import type { SxProps, Theme } from "@mui/material/styles";
import {
  DEFAULT_TRANSITION,
  HEADER_HEIGHT,
  MAX_WIDTH,
  SIDE_PADDING,
} from "../../theme/consts";

// Main section container - relative positioning context
export const sectionContainerSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  zIndex: 1,
  marginTop: { xs: "-2rem", md: "-3rem", "2xl": "-6rem" },
  scrollMarginTop: {
    xs: HEADER_HEIGHT,
    "2xl": `calc(${HEADER_HEIGHT} + 1rem)`,
  },
};

// Sticky background - stays in place while cards scroll over it
export const stickyBackgroundSx: SxProps<Theme> = {
  position: "sticky",
  top: HEADER_HEIGHT,
  height: `calc(100svh - ${HEADER_HEIGHT})`,
  width: "100%",
  zIndex: 0,
};

// Background video container
export const videoBackgroundSx: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 0,
  pointerEvents: "none",
  "& video": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
};

// Gradient overlay on video - fades from background color at edges (left/right)
export const videoOverlaySx: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 1,
  pointerEvents: "none",
  background: `
    linear-gradient(
      90deg,
      #f8fafc 0%,
      rgba(248, 250, 252, 0) 15%,
      rgba(248, 250, 252, 0) 85%,
      #f8fafc 100%
    )
  `,
};

// Sticky fade overlay - creates card fade effect at top and bottom edges
// Stays fixed while cards scroll underneath
export const stickyFadeOverlaySx: SxProps<Theme> = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 2,
  pointerEvents: "none",
  background: `
    linear-gradient(
      180deg,
      #f8fafc 0%,
      rgba(248, 250, 252, 0) 20%,
      rgba(248, 250, 252, 0) 80%,
      #f8fafc 100%
    )
  `,
};

// Cards layer - scrolls over sticky background
// Negative margin pulls cards up over the sticky video background
export const cardsGradientOverlaySx: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
  marginTop: `calc(-100svh + ${HEADER_HEIGHT})`,
  pt: "1rem",
  pointerEvents: "none",
  // Allow pointer events on children (cards)
  "& > *": {
    pointerEvents: "auto",
  },
};

// Section header with CSS scroll-snap
export const sectionHeaderSx: SxProps<Theme> = {
  width: "100%",
  maxWidth: MAX_WIDTH,
  margin: "0 auto",
  py: { xs: "1rem", md: "2rem" },
  px: {
    xs: SIDE_PADDING.xs,
    md: SIDE_PADDING.md,
    xl: SIDE_PADDING.xl,
  },
  // top: "128px",
  scrollMarginTop: {
    xs: HEADER_HEIGHT,
    "2xl": `calc(${HEADER_HEIGHT} + 1rem)`,
  },
};

// Cards container - simple stack layout
export const cardsContainerSx: SxProps<Theme> = {
  gap: { xs: "1.25rem", md: "2rem", "2xl": "3rem" },
  maxWidth: MAX_WIDTH,
  width: "100%",
  margin: "0 auto",
  pb: { xs: "4rem", md: "8rem" },
  px: {
    xs: SIDE_PADDING.xs,
    md: SIDE_PADDING.md,
    xl: SIDE_PADDING.xl,
  },
};

// Base card styles
const baseCardSx: SxProps<Theme> = {
  position: "relative",
  borderRadius: "2rem",
  padding: "1.25rem 1.75rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  maxWidth: { xs: "100%", md: "72%" },
  background: "#d9d9d98c",
  backdropFilter: "blur(30px)",
  border: "1px solid #FFFFFF",
  transition: DEFAULT_TRANSITION,
};

// Left-aligned card
export const cardLeftSx: SxProps<Theme> = {
  ...baseCardSx,
  alignSelf: "flex-start",
};

// Right-aligned card
export const cardRightSx: SxProps<Theme> = {
  ...baseCardSx,
  alignSelf: "flex-end",
};

// Card title
export const cardTitleSx: SxProps<Theme> = {
  fontSize: "1.375rem",
  fontWeight: 600,
  color: "text.primary",
  lineHeight: 1.25,
};

// Card content wrapper (features + benefits)
export const cardContentSx: SxProps<Theme> = {
  display: "flex",
  gap: "2rem",
  flexWrap: "wrap",
  "& p": {
    fontWeight: 400,
  },
};

// Card column (feature or benefit)
export const cardColumnSx: SxProps<Theme> = {
  flex: "1 1 280px",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

// Label arrow icon
export const labelArrowSx: SxProps<Theme> = {
  width: "0.75rem",
  height: "auto",
};
